import React, { useState, useEffect, useMemo } from "react"
import { Card, Row, Col, Button } from "react-bootstrap"
import { useIntl } from "react-intl"
import CurrencySelector from "./CurrencySelector"
import TimeRangeButtons from "./TimeRangeButtons"
import CustomChartTooltip from "./CustomChartTooltip"
import HistoricalDataTable from "./HistoricalDataTable"
import Loading from "./loading"
import ErrorMessage from "./ErrorMessage"
import { fetchHistoricalExchangeRates, fetchLatestExchangeRates, validateDataConsistency } from "../services/exchangeRateService"
import { formatXAxisTick, formatYAxisTick } from "../utils/formatters"
import { interpolateGaps, hasGaps, getGapStatistics } from "../utils/dataGapHandler"

const HistoricalExchangeRateChart = () => {
  const intl = useIntl()
  const [selectedCurrency, setSelectedCurrency] = useState("BTC")
  const [selectedRange, setSelectedRange] = useState("7d")
  const [chartData, setChartData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showDataTable, setShowDataTable] = useState(false)
  const [dataWarning, setDataWarning] = useState(null)
  const [gapInfo, setGapInfo] = useState(null)
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)
  const [activeCandle, setActiveCandle] = useState(null)

  const quoteCurrency = "EUR"
  const minSwipeDistance = 50
  const chartWidth = 900
  const chartHeight = 400
  const chartMargin = { top: 20, right: 24, bottom: 52, left: 90 }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      setDataWarning(null)
      setGapInfo(null)
      setActiveCandle(null)

      try {
        const response = await fetchHistoricalExchangeRates(
          selectedCurrency,
          quoteCurrency,
          selectedRange
        )

        let historicalData = response.data || []

        if (historicalData.length > 0 && hasGaps(historicalData, selectedRange)) {
          const gapStats = getGapStatistics(historicalData, selectedRange)

          setGapInfo({
            count: gapStats.count,
            message: intl.formatMessage(
              { id: "Data contains {count} gap(s). Missing data points have been interpolated." },
              { count: gapStats.count }
            ),
          })

          historicalData = interpolateGaps(historicalData, selectedRange)
        }

        setChartData(historicalData)

        if (historicalData.length > 0) {
          try {
            const latestRates = await fetchLatestExchangeRates()
            const currentRate = latestRates.find(
              rate => rate.currencyFrom === selectedCurrency && rate.currencyTo === quoteCurrency
            )

            if (currentRate) {
              const latestHistoricalPoint = historicalData[historicalData.length - 1]
              const validation = validateDataConsistency(
                latestHistoricalPoint.value,
                currentRate.value
              )

              if (!validation.isValid) {
                setDataWarning({
                  type: "inconsistent",
                  message: intl.formatMessage(
                    { id: "Warning: Historical data may be stale. Latest chart value differs from current rate by {percent}%." },
                    { percent: validation.percentageDiff.toFixed(2) }
                  ),
                })
              }
            }
          } catch (validationError) {
            console.warn("Data validation failed:", validationError)
          }
        }
      } catch (err) {
        setError(err.message || "Failed to load historical data")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [selectedCurrency, selectedRange, quoteCurrency, intl])

  const handleCurrencyChange = newCurrency => {
    setSelectedCurrency(newCurrency)
  }

  const handleTimeRangeChange = newRange => {
    setSelectedRange(newRange)
  }

  const handleRetry = () => {
    setError(null)
    setDataWarning(null)
    setGapInfo(null)
    setLoading(true)
    setActiveCandle(null)

    const fetchData = async () => {
      try {
        const response = await fetchHistoricalExchangeRates(
          selectedCurrency,
          quoteCurrency,
          selectedRange
        )

        let historicalData = response.data || []

        if (historicalData.length > 0 && hasGaps(historicalData, selectedRange)) {
          const gapStats = getGapStatistics(historicalData, selectedRange)

          setGapInfo({
            count: gapStats.count,
            message: intl.formatMessage(
              { id: "Data contains {count} gap(s). Missing data points have been interpolated." },
              { count: gapStats.count }
            ),
          })

          historicalData = interpolateGaps(historicalData, selectedRange)
        }

        setChartData(historicalData)

        if (historicalData.length > 0) {
          try {
            const latestRates = await fetchLatestExchangeRates()
            const currentRate = latestRates.find(
              rate => rate.currencyFrom === selectedCurrency && rate.currencyTo === quoteCurrency
            )

            if (currentRate) {
              const latestHistoricalPoint = historicalData[historicalData.length - 1]
              const validation = validateDataConsistency(
                latestHistoricalPoint.value,
                currentRate.value
              )

              if (!validation.isValid) {
                setDataWarning({
                  type: "inconsistent",
                  message: intl.formatMessage(
                    { id: "Warning: Historical data may be stale. Latest chart value differs from current rate by {percent}%." },
                    { percent: validation.percentageDiff.toFixed(2) }
                  ),
                })
              }
            }
          } catch (validationError) {
            console.warn("Data validation failed:", validationError)
          }
        }
      } catch (err) {
        setError(err.message || "Failed to load historical data")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }

  const formatXAxisTickLabel = timestamp => {
    return formatXAxisTick(timestamp, selectedRange, intl.locale)
  }

  const formatYAxisTickLabel = value => {
    return formatYAxisTick(value, quoteCurrency, intl.locale)
  }

  const candlestickData = useMemo(() => {
    return chartData.map((entry, index) => {
      const previousValue = index > 0 ? chartData[index - 1].value : entry.value
      const open = previousValue
      const close = entry.value
      const bodyRange = Math.abs(close - open)
      const wickPadding = Math.max(bodyRange * 0.35, Math.abs(close || open) * 0.0025, 0.000001)

      return {
        ...entry,
        previousValue,
        open,
        close,
        high: Math.max(open, close) + wickPadding,
        low: Math.min(open, close) - wickPadding,
        direction: close >= open ? "up" : "down",
      }
    })
  }, [chartData])

  const chartGeometry = useMemo(() => {
    if (candlestickData.length === 0) {
      return null
    }

    const innerWidth = chartWidth - chartMargin.left - chartMargin.right
    const innerHeight = chartHeight - chartMargin.top - chartMargin.bottom
    const minValue = Math.min(...candlestickData.map(point => point.low))
    const maxValue = Math.max(...candlestickData.map(point => point.high))
    const valueRange = maxValue - minValue || Math.max(maxValue * 0.05, 1)
    const paddedMin = minValue - valueRange * 0.05
    const paddedMax = maxValue + valueRange * 0.05
    const paddedRange = paddedMax - paddedMin || 1
    const step = candlestickData.length > 1 ? innerWidth / (candlestickData.length - 1) : innerWidth / 2
    const candleWidth = Math.max(6, Math.min(18, step * 0.6))
    const yTickCount = 5
    const yTicks = Array.from({ length: yTickCount }, (_, index) => {
      const ratio = index / (yTickCount - 1)
      const value = paddedMax - paddedRange * ratio

      return {
        value,
        y: chartMargin.top + innerHeight * ratio,
      }
    })
    const xTickTarget = Math.min(6, candlestickData.length)
    const xTickStep = Math.max(1, Math.ceil(candlestickData.length / xTickTarget))
    const xTicks = candlestickData.filter((_, index) => {
      return index % xTickStep === 0 || index === candlestickData.length - 1
    })

    const toY = value => {
      return chartMargin.top + ((paddedMax - value) / paddedRange) * innerHeight
    }

    const candles = candlestickData.map((point, index) => {
      const x = chartMargin.left + (candlestickData.length === 1 ? innerWidth / 2 : step * index)
      const openY = toY(point.open)
      const closeY = toY(point.close)
      const highY = toY(point.high)
      const lowY = toY(point.low)
      const bodyTop = Math.min(openY, closeY)
      const bodyHeight = Math.max(Math.abs(closeY - openY), 2)

      return {
        ...point,
        x,
        openY,
        closeY,
        highY,
        lowY,
        bodyTop,
        bodyHeight,
      }
    })

    return {
      candles,
      innerWidth,
      innerHeight,
      yTicks,
      xTicks,
      candleWidth,
      toY,
    }
  }, [candlestickData])

  const onTouchStart = e => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = e => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe || isRightSwipe) {
      const timeRanges = ["24h", "7d", "30d", "1y"]
      const currentIndex = timeRanges.indexOf(selectedRange)

      if (isLeftSwipe && currentIndex < timeRanges.length - 1) {
        handleTimeRangeChange(timeRanges[currentIndex + 1])
      } else if (isRightSwipe && currentIndex > 0) {
        handleTimeRangeChange(timeRanges[currentIndex - 1])
      }
    }
  }

  return (
    <Card className="mb-4">
      <Card.Body>
        <Card.Title id="chart-title">
          <i className="fas fa-chart-line me-2" aria-hidden="true"></i>
          {intl.formatMessage({ id: "Historical Exchange Rates" })}
        </Card.Title>

        <Row className="mb-3">
          <Col xs={12} md={6} className="mb-2 mb-md-0">
            <CurrencySelector
              value={selectedCurrency}
              onChange={handleCurrencyChange}
            />
          </Col>
          <Col xs={12} md={6}>
            <TimeRangeButtons
              selected={selectedRange}
              onChange={handleTimeRangeChange}
            />
          </Col>
        </Row>

        {loading && <Loading />}
        {error && <ErrorMessage error={error} onRetry={handleRetry} />}

        {!loading && !error && gapInfo && (
          <div className="alert alert-info d-flex align-items-center" role="alert">
            <i className="fas fa-info-circle me-2" aria-hidden="true"></i>
            <div>{gapInfo.message}</div>
          </div>
        )}

        {!loading && !error && dataWarning && (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
            <div>{dataWarning.message}</div>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="d-md-none text-center text-muted small mb-2">
              <i className="fas fa-hand-pointer me-1" aria-hidden="true"></i>
              {intl.formatMessage({ id: "Swipe left or right to change time range" })}
            </div>

            <div
              className="chart-container position-relative"
              role="img"
              aria-labelledby="chart-title"
              aria-describedby="chart-description"
              tabIndex={0}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onMouseLeave={() => setActiveCandle(null)}
            >
              <span id="chart-description" className="visually-hidden">
                {intl.formatMessage(
                  { id: "Interactive chart showing {currency} to {quote} exchange rates over {range}" },
                  { currency: selectedCurrency, quote: quoteCurrency, range: selectedRange }
                )}
              </span>
              {chartData.length === 0 || !chartGeometry ? (
                <div className="text-center text-muted py-5">
                  {intl.formatMessage({ id: "No data available" })}
                </div>
              ) : (
                <>
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    width="100%"
                    height={chartHeight}
                    role="application"
                    aria-label={intl.formatMessage(
                      { id: "Interactive chart showing {currency} exchange rates" },
                      { currency: selectedCurrency }
                    )}
                    preserveAspectRatio="none"
                  >
                    <rect x="0" y="0" width={chartWidth} height={chartHeight} fill="#ffffff" />

                    {chartGeometry.yTicks.map(tick => (
                      <g key={`y-${tick.value}`}>
                        <line
                          x1={chartMargin.left}
                          y1={tick.y}
                          x2={chartWidth - chartMargin.right}
                          y2={tick.y}
                          stroke="#e9ecef"
                          strokeDasharray="4 4"
                        />
                        <text
                          x={chartMargin.left - 12}
                          y={tick.y + 4}
                          textAnchor="end"
                          fontSize="12"
                          fill="#6c757d"
                        >
                          {formatYAxisTickLabel(tick.value)}
                        </text>
                      </g>
                    ))}

                    {chartGeometry.xTicks.map(tick => {
                      const candle = chartGeometry.candles.find(item => item.timestamp === tick.timestamp)

                      if (!candle) {
                        return null
                      }

                      return (
                        <g key={`x-${tick.timestamp}`}>
                          <line
                            x1={candle.x}
                            y1={chartMargin.top}
                            x2={candle.x}
                            y2={chartHeight - chartMargin.bottom}
                            stroke="#f1f3f5"
                          />
                          <text
                            x={candle.x}
                            y={chartHeight - chartMargin.bottom + 18}
                            textAnchor="middle"
                            fontSize="12"
                            fill="#6c757d"
                          >
                            {formatXAxisTickLabel(tick.timestamp)}
                          </text>
                        </g>
                      )
                    })}

                    <line
                      x1={chartMargin.left}
                      y1={chartHeight - chartMargin.bottom}
                      x2={chartWidth - chartMargin.right}
                      y2={chartHeight - chartMargin.bottom}
                      stroke="#adb5bd"
                    />
                    <line
                      x1={chartMargin.left}
                      y1={chartMargin.top}
                      x2={chartMargin.left}
                      y2={chartHeight - chartMargin.bottom}
                      stroke="#adb5bd"
                    />

                    {chartGeometry.candles.map((candle, index) => {
                      const isActive = activeCandle && activeCandle.index === index
                      const bodyFill = candle.direction === "up" ? "#198754" : "#dc3545"
                      const wickStroke = candle.direction === "up" ? "#157347" : "#b02a37"

                      return (
                        <g
                          key={candle.timestamp}
                          style={{ cursor: "pointer" }}
                          onMouseEnter={() => setActiveCandle({ index, candle })}
                          onFocus={() => setActiveCandle({ index, candle })}
                        >
                          <line
                            x1={candle.x}
                            y1={candle.highY}
                            x2={candle.x}
                            y2={candle.lowY}
                            stroke={wickStroke}
                            strokeWidth={2}
                          />
                          <rect
                            x={candle.x - chartGeometry.candleWidth / 2}
                            y={candle.bodyTop}
                            width={chartGeometry.candleWidth}
                            height={candle.bodyHeight}
                            fill={bodyFill}
                            fillOpacity={candle.interpolated ? 0.45 : 0.9}
                            stroke={wickStroke}
                            strokeWidth={isActive ? 2.5 : 1.2}
                            rx={1}
                          />
                          <rect
                            x={candle.x - chartGeometry.candleWidth / 2 - 8}
                            y={chartMargin.top}
                            width={chartGeometry.candleWidth + 16}
                            height={chartGeometry.innerHeight}
                            fill="transparent"
                            tabIndex={0}
                            aria-label={`${selectedCurrency}/${quoteCurrency} ${formatXAxisTickLabel(candle.timestamp)}`}
                          />
                        </g>
                      )
                    })}

                    <text
                      x={chartWidth / 2}
                      y={chartHeight - 12}
                      textAnchor="middle"
                      fontSize="12"
                      fill="#6c757d"
                    >
                      {intl.formatMessage({ id: "Time" })}
                    </text>

                    <text
                      x={chartWidth - chartMargin.right}
                      y={chartMargin.top - 4}
                      textAnchor="end"
                      fontSize="12"
                      fill="#6c757d"
                    >
                      {selectedCurrency}/{quoteCurrency}
                    </text>
                  </svg>

                  {activeCandle && (
                    <div
                      style={{
                        position: "absolute",
                        left: `${Math.min(78, Math.max(10, (activeCandle.candle.x / chartWidth) * 100))}%`,
                        top: `${Math.min(56, Math.max(6, ((activeCandle.candle.bodyTop - 48) / chartHeight) * 100))}%`,
                        transform: "translate(-50%, -100%)",
                        pointerEvents: "none",
                        zIndex: 2,
                      }}
                    >
                      <CustomChartTooltip
                        active
                        payload={[
                          {
                            payload: activeCandle.candle,
                            value: activeCandle.candle.close,
                          },
                        ]}
                        quoteCurrency={quoteCurrency}
                        timeRange={selectedRange}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="text-center mt-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowDataTable(!showDataTable)}
                aria-expanded={showDataTable}
                aria-controls="historical-data-table"
              >
                <i className={`fas fa-${showDataTable ? "chart-line" : "table"} me-2`} aria-hidden="true"></i>
                {intl.formatMessage({
                  id: showDataTable ? "Show Chart" : "Show Data Table",
                })}
              </Button>
            </div>

            {showDataTable && (
              <div id="historical-data-table">
                <HistoricalDataTable
                  data={chartData}
                  currency={selectedCurrency}
                  quoteCurrency={quoteCurrency}
                  timeRange={selectedRange}
                />
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  )
}

export default HistoricalExchangeRateChart
